import { Injectable } from '@angular/core';
import { GraphQLModule } from '../../../core/auth/graphql.module';
import { createClient, Client } from 'graphql-ws';
import { Observable } from 'rxjs';

/**
 * Live dispatch subscription: the backend fires `dayEventsUpdated` whenever
 * calendar events in the subscriber's zone change (created / edited / removed),
 * carrying the YYYY-MM-DD days the mutation touched. The board refetches the
 * viewed day when it intersects — the server state replaces the optimistic
 * state while local pending changes are preserved by the reducer's replay.
 *
 * Uses the app's shared GraphQLModule websocket client directly. Apollo's
 * subscribe path waits on module readiness/auth in ways that kept this
 * subscription silent; the raw client is verified end-to-end.
 */

export interface DayEventsMutated {
	zone: string;
	dates: string[];
	mutation: 'created' | 'edited' | 'removed';
}

const DAY_EVENTS_UPDATED_QUERY = `
	subscription DayEventsUpdated {
		dayEventsUpdated {
			zone
			dates
			mutation
		}
	}
`;

@Injectable({ providedIn: 'root' })
export class DayEventsUpdatedGQL {
	constructor(private graphqlModule: GraphQLModule) {}

	/** The shared app websocket client (graphql-transport-ws, cookies auth). */
	private getClient(): Client {
		if (this.graphqlModule.wsClient) { return this.graphqlModule.wsClient; }
		const wsProtocol = location.protocol === 'http:' ? 'ws:' : 'wss:';
		const client = createClient({
			url: `${wsProtocol}//${location.host}/api/subscriptions`,
			retryAttempts: Infinity,
			shouldRetry: () => true,
			keepAlive: 10000,
			connectionParams: () => ({
				zone: this.graphqlModule.zone.value,
				role: this.graphqlModule.role.value,
			}),
		});
		this.graphqlModule.wsClient = client as any;
		return client;
	}

	/**
	 * Subscribe to day mutations. Emits the unwrapped
	 * { zone, dates, mutation } payload. The shared wsClient connects lazily;
	 * if the socket is down the subscription errors and the caller may retry.
	 */
	public subscribe(): Observable<DayEventsMutated> {
		return new Observable<DayEventsMutated>((observer) => {
			const client: Client = this.getClient();
			const unsubscribe = client.subscribe<{ dayEventsUpdated: DayEventsMutated }>(
				{
					query: DAY_EVENTS_UPDATED_QUERY,
					operationName: 'DayEventsUpdated',
				},
				{
					next: (res) => {
						const mutated = res?.data?.dayEventsUpdated;
						if (mutated) { observer.next(mutated); }
					},
					error: (err) => observer.error(err),
					complete: () => observer.complete(),
				},
			);
			return () => unsubscribe();
		});
	}
}
